import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";
import { useParams, useNavigate } from "react-router-dom";

export default function BomCreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [businessId, setBusinessId] = useState("");

  const [items, setItems] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingBom, setLoadingBom] = useState(false);
  const [margin, setMargin] = useState(0);

  const [rows, setRows] = useState([
    {
      itemName: "",
      variant: "",
      companyName: "",
      unit: "",
      quantity: "",
      rate: "",
    },
  ]);

  useEffect(() => {
    const storedBusinessId = localStorage.getItem('businessId');
    if (storedBusinessId) {
      setBusinessId(storedBusinessId);
    }
  }, []);

  useEffect(() => {
    if (businessId) {
      fetchInitialData();
    }
    // eslint-disable-next-line
  }, [businessId]);

  useEffect(() => {
    if (isEditMode && id && businessId) {
      fetchBomData();
    }
    // eslint-disable-next-line
  }, [isEditMode, id, businessId]);

  const fetchBomData = async () => {
    setLoadingBom(true);
    try {
      const response = await axiosInstance.get(`/api/bom/${id}`);
      if (response.data && response.data.Status === 1 && response.data.bom) {
        const bomData = response.data.bom;
        setName(bomData.name || "");
        setCode(bomData.code || "");
        setNotes(bomData.note || "");
        setMargin(bomData.margin || 0);

        if (Array.isArray(bomData.items) && bomData.items.length > 0) {
          const bomRows = bomData.items.map(item => ({
            itemName: item.item?._id || item.item,
            variant: item.variant?._id || item.variant,
            companyName: item.variant?.companyName || "",
            unit: item.variant?.unit?.name || "",
            quantity: item.qty || "",
            rate: item.variant?.rate || "",
          }));
          setRows(bomRows);
        }
      } else {
        toastError("Failed to fetch BOM data. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching BOM data:", error);
      toastError("Failed to fetch BOM data. Please try again.");
    } finally {
      setLoadingBom(false);
    }
  };

  const fetchInitialData = async () => {
    setLoadingData(true);
    try {
      await fetchItems();
    } catch {
      toastError("Failed to fetch initial data");
    } finally {
      setLoadingData(false);
    }
  };

  const fetchItems = async () => {
    try {
      let url = '/api/item?page=1&limit=100';
      if (businessId) {
        url += `&business=${businessId}`;
      }
      const response = await axiosInstance.get(url);
      if (response.data && response.data.Status === 1) {
        setItems(Array.isArray(response.data.items) ? response.data.items : []);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      toastError("Failed to fetch items");
    }
  };

  const getVariantsForItem = (itemId) => {
    const item = items.find(item => item._id === itemId);
    if (item && Array.isArray(item.variants)) {
      return item.variants.filter(variant => variant.isAvailable && variant.isshow);
    }
    return [];
  };

  const calculateTotals = () => {
    let subTotal = 0;
    let totalGst = 0;

    rows.forEach(row => {
      // Calculate line total whenever quantity and rate are present
      if (row.quantity && row.rate) {
        const quantity = parseFloat(row.quantity) || 0;
        const rate = parseFloat(row.rate) || 0;
        const lineTotal = quantity * rate;
        subTotal += lineTotal;

        // Try to get GST from selected variant if available, otherwise fall back to 0
        const selectedVariant = getVariantsForItem(row.itemName).find(v => v._id === row.variant);
        const gstRate = (selectedVariant && selectedVariant.gst) ? Number(selectedVariant.gst) : 0;
        if (gstRate) {
          totalGst += (lineTotal * gstRate) / 100;
        }
      }
    });

    const marginAmount = (subTotal * margin) / 100;
    const generalTotal = subTotal + totalGst + marginAmount;

    return {
      subTotal,
      margin: marginAmount,
      totalGst,
      generalTotal
    };
  };

  const totals = calculateTotals();

  const handleRowChange = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx][field] = value;
      if (field === "itemName") {
        updated[idx].variant = "";
        updated[idx].companyName = "";
        updated[idx].rate = "";
        updated[idx].unit = "";
      }
      if (field === "variant" && value) {
        const selectedVariant = getVariantsForItem(updated[idx].itemName).find(v => v._id === value);
        if (selectedVariant) {
          updated[idx].companyName = selectedVariant.companyName || "";
          updated[idx].rate = selectedVariant.rate || "";
          updated[idx].unit = selectedVariant.unit ? selectedVariant.unit.name : "";
        }
      }
      return updated;
    });
  };

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "" }
    ]);
  };

  const handleRemoveRow = idx => {
    if (rows.length === 1) {
      // If it's the last row, just clear its values instead of removing
      setRows([
        { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "" }
      ]);
    } else {
      setRows(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
        navigate('/user/bom');
    } else {
      // If creating, ask for confirmation if form has data
      const hasFormData = name || code || notes || margin > 0 || 
        rows.some(row => row.itemName || row.variant || row.quantity);
      
      if (hasFormData) {
        {
          setName("");
          setCode("");
          setNotes("");
          setMargin(0);
          setRows([
            { itemName: "", variant: "", companyName: "", unit: "", quantity: "", rate: "" }
          ]);
        }
      } else {
        // Navigate back if no data entered
        navigate('/user/bom');
      }
    }
  };

  const prepareBOMPayload = () => {
    const validRows = rows.filter(row => 
      row.itemName && 
      row.variant && 
      row.quantity && 
      parseFloat(row.quantity) > 0
    );
    
    const items = validRows.map(row => ({
      item: row.itemName,
      variant: row.variant,
      qty: parseFloat(row.quantity) || 0
    }));

    // Calculate totals for the payload
    const calculations = calculateTotals();
    
    return {
      name: name.trim(),
      code: code.trim(),
      business: businessId,
      items,
      note: notes.trim() || undefined,
      totalAmount: calculations.subTotal,
      grandAmount: calculations.generalTotal,
      margin: margin,
      marginAmount: calculations.margin,
      gst: calculations.totalGst
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!name.trim()) {
      toastError("Please enter a BOM name");
      return;
    }

    if (!code.trim()) {
      toastError("Please enter a BOM code");
      return;
    }

    // Validate items
    const validRows = rows.filter(row => row.itemName && row.variant && row.quantity);
    if (validRows.length === 0) {
      toastError("Please add at least one item with complete information (Item, Variant, and Quantity)");
      return;
    }

    // Check for invalid quantities
    const invalidQuantities = validRows.filter(row => {
      const qty = parseFloat(row.quantity);
      return isNaN(qty) || qty <= 0;
    });

    if (invalidQuantities.length > 0) {
      toastError("Please enter valid quantities (greater than 0) for all items");
      return;
    }

    setLoading(true);

    try {
      const payload = prepareBOMPayload();

      let response;
      if (isEditMode) {
        response = await axiosInstance.put(`/api/bom/${id}`, payload);
      } else {
        response = await axiosInstance.post('/api/bom', payload);
      }

      if (response.data && response.data.Status === 1) {
        toastSuccess(`BOM ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate('/user/bom');
      } else {
        toastError(response.data?.Message || `Failed to ${isEditMode ? 'update' : 'create'} BOM`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} BOM:`, error);
      toastError(`Error ${isEditMode ? 'updating' : 'saving'} BOM: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 fw-bold">{isEditMode ? 'Edit Bill Of Material' : 'Add Bill Of Material'}</h1>
        <a href="/user/bom" className="text-decoration-none text-secondary">
            &larr; Bom
          </a>
      </div>
      <div className="card shadow-sm rounded-4 p-4" style={{ background: "#F6F6F6", border: "1.5px solid #BBBFD2" }}>
        <form onSubmit={handleSave}>
          <div className="row g-3 mb-2">
            <div className="col-md-4">
              <label className="form-label text-muted">Name *</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-muted">Code *</label>
              <input
                type="text"
                className="form-control"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="e.g., BOM-001"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-muted">Notes</label>
              <input
                type="text"
                className="form-control"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <hr className="mt-2 mb-0 border border-secondary opacity-20" />
          <div className="">
            {(loadingData || loadingBom) ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">
                  {loadingBom ? 'Loading BOM data...' : 'Loading items and variants...'}
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}> </th>
                        <th>Item Name</th>
                        <th>Variant</th>
                        <th>Company Name</th>
                        <th>Unit</th>
                        <th>Quantity</th>
                        <th>Rate(₹)</th>
                          <th>Total(₹)</th>
                        <th style={{ width: 60 }}> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <select
                              className="form-select"
                              value={row.itemName}
                              onChange={e => handleRowChange(idx, "itemName", e.target.value)}
                            >
                              <option value="">Select Item</option>
                              {items.filter(item => item.isAvailable && item.isshow).map(item => (
                                <option key={item._id} value={item._id}>
                                  {item.name} ({item.code})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              value={row.variant}
                              onChange={e => handleRowChange(idx, "variant", e.target.value)}
                              disabled={!row.itemName}
                            >
                              <option value="">Select Variant</option>
                              {getVariantsForItem(row.itemName).map(variant => (
                                <option key={variant._id} value={variant._id}>
                                  {variant.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Company Name"
                              value={row.companyName}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Unit"
                              value={row.unit}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Quantity"
                              value={row.quantity}
                              onChange={e => handleRowChange(idx, "quantity", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Rate(₹)"
                              value={row.rate}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Total"
                              value={(Number(row.quantity || 0) * Number(row.rate || 0)).toFixed(2)}
                              readOnly
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-danger px-3"
                              style={{ fontWeight: 700, fontSize: 22 }}
                              onClick={() => handleRemoveRow(idx)}
                              disabled={rows.length === 1}
                            >–</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary mt-3"
                  style={{ width: 40, height: 40, fontSize: 22, padding: 0 }}
                  onClick={handleAddRow}
                >
                  +
                </button>
                <div className="d-flex flex-column align-items-end mt-3 fs-5">
                  <div className="mb-3">
                    Sub Total: <span className="fw-semibold">₹{totals.subTotal.toFixed(2)}</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
                    <label
                      className="form-label mb-0 fw-light text-end text-nowrap"
                      style={{ width: "150px" }}
                    >
                      Gross Margin % :
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm w-25 rounded-2"
                      placeholder="0"
                      value={margin}
                      onChange={e => setMargin(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="ms-3 text-muted" style={{ minWidth: 160 }}>
                      Margin Amount: <span className="fw-semibold">₹{(totals.margin || 0).toFixed(2)}</span>
                    </div>
                  <div className="mb-3">
                    GST Total: <span className="fw-semibold">₹{totals.totalGst.toFixed(2)}</span>
                  </div>
                  <div className="fw-bold fs-4 text-primary mt-2">
                    General Total: <span>₹{totals.generalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading || loadingBom}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading || loadingBom}>
              {loading ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}